import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Package, MapPin, TrendingUp, FileText, 
  Truck, DollarSign, Calendar, CheckCircle, Award,
  Brain, BarChart3, ShieldCheck, AlertTriangle, Heart, MessageSquare
} from 'lucide-react';
import { getMineralSymbol, getMineralColor } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MineralDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [mineral, setMineral] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [marketContext, setMarketContext] = useState(null);

  useEffect(() => {
    fetchMineral();
  }, [id]);

  const fetchMineral = async () => {
    try {
      const [mineralRes, scoreRes] = await Promise.all([
        axios.get(`${API}/minerals/${id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/minerals/${id}/score`, { headers: getAuthHeaders() }).catch(() => ({ data: null }))
      ]);
      setMineral(mineralRes.data);
      if (scoreRes.data) setAiScore(scoreRes.data);
      
      try {
        const marketRes = await axios.get(`${API}/markets/commodities`, { headers: getAuthHeaders() });
        const relatedCommodity = marketRes.data.find(c => 
          mineralRes.data.commodity.includes(c.commodity.split(' ')[0])
        );
        setMarketContext(relatedCommodity);
      } catch (err) {}
    } catch (error) {
      console.error('Failed to fetch mineral:', error);
      toast.error('Failed to load mineral details');
    } finally {
      setLoading(false);
    }
  };

  const runAIScoring = async () => {
    setScoring(true);
    try {
      const res = await axios.post(`${API}/minerals/${id}/score`, {}, { headers: getAuthHeaders() });
      setAiScore(res.data);
      toast.success('AI scoring complete');
    } catch (error) {
      toast.error('Scoring failed');
    } finally {
      setScoring(false);
    }
  };

  const handleRequestInfo = async () => {
    try {
      await axios.post(`${API}/interest/request-info`, { entity_id: id, entity_type: 'mineral', request_type: 'documents', message: 'Requesting additional documents and information' }, { headers: getAuthHeaders() });
      toast.success('Information request submitted. Admin will be notified.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    }
  };

  const handleExpressInterest = async () => {
    try {
      await axios.post(`${API}/interest/express`, { entity_id: id, entity_type: 'mineral', message: '' }, { headers: getAuthHeaders() });
      toast.success('Interest expressed successfully. Admin will be notified.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to express interest');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading mineral details...</div>
      </div>
    );
  }

  if (!mineral) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Mineral not found</div>
      </div>
    );
  }

  const statusColor = mineral.status === 'available' 
    ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
    : mineral.status === 'negotiating'
    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';

  return (
    <div className="p-6 space-y-6" data-testid="mineral-detail-page">
      <div className="flex items-center space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/minerals')}
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
        </Button>
        <div className="flex-1">
          <div className="flex items-center space-x-4">
            <div
              className="w-16 h-16 rounded flex items-center justify-center font-mono font-bold text-white text-2xl"
              style={{ backgroundColor: getMineralColor(mineral.commodity) }}
            >
              {getMineralSymbol(mineral.commodity)}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-3xl font-bold tracking-tight">{mineral.commodity}</h1>
                {mineral.verification_status === 'verified' && <Badge className="bg-green-100 text-green-700 text-[10px]">Verified by INMX</Badge>}
                {mineral.listing_type === 'demo' && <Badge variant="outline" className="text-[10px] border-orange-300 text-orange-600" data-testid="demo-badge">Demo Listing</Badge>}
                {mineral.commodity_category && <Badge variant="outline" className="text-[10px]">{mineral.commodity_category}</Badge>}
              </div>
              <div className="flex items-center space-x-3 mt-2 text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <MapPin size={16} />
                  <span>{mineral.origin_country}</span>
                </div>
                <span>•</span>
                <Badge className={statusColor}>
                  {mineral.status}
                </Badge>
              </div>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleExpressInterest} variant="default" data-testid="express-interest-btn">
            <Heart size={16} className="mr-2" />
            Express Interest
          </Button>
          <Button onClick={handleRequestInfo} variant="outline" data-testid="request-info-button">
            <MessageSquare size={16} className="mr-2" />
            Request Additional Info
          </Button>
        </div>
      </div>

      {marketContext && (
        <Card className="border-l-4 border-l-accent">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Market Context</CardTitle>
              <Badge variant="outline">Live Data</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{marketContext.benchmark}</p>
                <p className="text-2xl font-bold font-mono mt-1">
                  {marketContext.price.toLocaleString()} {marketContext.unit}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">30-day change</p>
                <p className={`text-lg font-mono font-semibold ${marketContext.change_30d >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {marketContext.change_30d >= 0 ? '+' : ''}{marketContext.change_30d}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="grade-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              <Award size={16} className="mr-2" />
              Grade/Specification
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mineral.grade}</div>
          </CardContent>
        </Card>

        <Card data-testid="volume-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              <Package size={16} className="mr-2" />
              Annual Volume
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {mineral.volume.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">{mineral.volume_unit}</p>
          </CardContent>
        </Card>

        <Card data-testid="delivery-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              <Truck size={16} className="mr-2" />
              Delivery Terms
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold">{mineral.delivery_basis}</div>
          </CardContent>
        </Card>

        <Card data-testid="contract-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center">
              <Calendar size={16} className="mr-2" />
              Contract Duration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-semibold">{mineral.contract_duration}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="ai-score" data-testid="ai-score-tab">AI Score</TabsTrigger>
          <TabsTrigger value="commercial" data-testid="commercial-tab">Commercial Terms</TabsTrigger>
          <TabsTrigger value="certification" data-testid="certification-tab">Certification</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Product Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm leading-relaxed">{mineral.description}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Key Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Origin Country</p>
                <p className="text-sm mt-1">{mineral.origin_country}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Target Buyers</p>
                <p className="text-sm mt-1">{mineral.target_buyers}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Financing Need</p>
                {mineral.financing_need ? (
                  <div className="mt-1">
                    <Badge className="mb-2">Financing Required</Badge>
                    {mineral.financing_amount && (
                      <p className="text-lg font-bold font-mono mt-2">
                        ${mineral.financing_amount.toLocaleString()}
                      </p>
                    )}
                  </div>
                ) : (
                  <Badge className="mt-1">No Financing Required</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-score" className="space-y-4">
          {!aiScore ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Brain size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">AI Commodity Scoring</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">
                  Run AI analysis to score this listing on market demand, supply risk, buyer interest, logistics feasibility, and commercial attractiveness.
                </p>
                <Button onClick={runAIScoring} disabled={scoring} data-testid="run-mineral-score-btn">
                  <Brain size={16} className={`mr-2 ${scoring ? 'animate-pulse' : ''}`} />
                  {scoring ? 'Analyzing...' : 'Run AI Scoring'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2 text-base"><BarChart3 size={18} />Score Breakdown</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { label: 'Market Demand', value: aiScore.market_demand_score, weight: '25%' },
                    { label: 'Supply Risk', value: aiScore.supply_risk_score, weight: '20%' },
                    { label: 'Buyer Interest', value: aiScore.buyer_interest_score, weight: '25%' },
                    { label: 'Logistics Feasibility', value: aiScore.logistics_feasibility_score, weight: '15%' },
                    { label: 'Commercial Attractiveness', value: aiScore.commercial_attractiveness_score, weight: '15%' }
                  ].map(item => (
                    <div key={item.label}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm">{item.label} <span className="text-xs text-muted-foreground">({item.weight})</span></span>
                        <span className="text-sm font-mono font-bold">{item.value}</span>
                      </div>
                      <Progress value={item.value} className="h-2" />
                    </div>
                  ))}
                  <Separator />
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">Overall Score</span>
                    <span className="text-2xl font-mono font-bold">{aiScore.overall_score}<span className="text-sm text-muted-foreground">/100</span></span>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="flex items-center gap-2 text-base"><ShieldCheck size={18} />Assessment</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-2">
                    <Badge className={`text-base px-4 py-2 ${
                      aiScore.recommendation === 'strong_buy' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      aiScore.recommendation === 'buy' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      aiScore.recommendation === 'hold' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>{aiScore.recommendation?.replace('_', ' ').toUpperCase()}</Badge>
                  </div>
                  <p className="text-sm leading-relaxed">{aiScore.summary}</p>
                  {aiScore.strengths?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold mb-1 flex items-center gap-1"><CheckCircle size={14} className="text-green-500" />Strengths</p>
                      {aiScore.strengths.map((s, i) => <p key={i} className="text-sm text-muted-foreground ml-5">+ {s}</p>)}
                    </div>
                  )}
                  {aiScore.risk_flags?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold mb-1 flex items-center gap-1"><AlertTriangle size={14} className="text-yellow-500" />Risk Flags</p>
                      {aiScore.risk_flags.map((r, i) => <p key={i} className="text-sm text-muted-foreground ml-5">! {r}</p>)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>


        <TabsContent value="commercial" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Commercial Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pricing Basis</p>
                <p className="text-sm mt-1 font-mono">{mineral.pricing_basis}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Delivery Basis</p>
                <p className="text-sm mt-1">{mineral.delivery_basis}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Contract Duration</p>
                <p className="text-sm mt-1">{mineral.contract_duration}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Annual Volume</p>
                <p className="text-sm mt-1 font-mono">
                  {mineral.volume.toLocaleString()} {mineral.volume_unit}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="certification" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CheckCircle size={20} className="mr-2 text-green-500" />
                Certifications & Standards
              </CardTitle>
            </CardHeader>
            <CardContent>
              {mineral.certification && mineral.certification.length > 0 ? (
                <div className="space-y-2">
                  {mineral.certification.map((cert, index) => (
                    <div key={index} className="flex items-start space-x-2 p-3 border border-border rounded-sm">
                      <CheckCircle size={16} className="text-green-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">{cert}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No certifications listed</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Quality Assurance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                  <span className="text-sm">Grade Specification</span>
                  <Badge variant="outline">{mineral.grade}</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                  <span className="text-sm">Quality Control</span>
                  <Badge variant="outline">Third-party inspection available</Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-sm">
                  <span className="text-sm">Sampling</span>
                  <Badge variant="outline">SGS/Bureau Veritas certified</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
